import { Component, OnInit, Input, ChangeDetectionStrategy, HostListener, AfterViewInit, OnDestroy, ChangeDetectorRef, ElementRef, NgZone } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-trailer',
  templateUrl: './trailer.component.html',
  styleUrls: ['./trailer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrailerComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() trailer;

  width = 665;
  height = 375;
  resizeTimeout;
  setupTimeout;
  containerResizeObserver: any;
  private destroyed = false;

  trailerSource: SafeResourceUrl = null;

  constructor (
    private domSanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private el: ElementRef,
    private ngZone: NgZone
  ) {
  }

  private getParentContainer(className: string): HTMLElement {
    let element = this.el.nativeElement;
    while (element && element.parentElement) {
      element = element.parentElement;
      if (element.classList && element.classList.contains(className)) {
        return element;
      }
    }
    return null;
  }

  updateTrailerSize() {
    if (this.destroyed) {
      return;
    }
    const scrollContainer = this.getParentContainer("cm-container");

    if (scrollContainer) {
      const marginRight = 7;
      const maxTrailerWidth = 665;

      let containerWidth = scrollContainer.clientWidth || scrollContainer.offsetWidth;

      const rect = scrollContainer.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || containerWidth;
      const visibleLeft = Math.max(rect.left, 0);
      const visibleRight = Math.min(rect.right, viewportWidth);
      const intersectionWidth = Math.max(visibleRight - visibleLeft, 0);

      if (intersectionWidth > 0 && intersectionWidth < containerWidth) {
        containerWidth = intersectionWidth;
      }

      const availableWidth = Math.max(containerWidth - marginRight, 0);

      if (availableWidth < maxTrailerWidth) {
        this.width = Math.max(availableWidth, 240);
        this.height = 9 / 16 * this.width;
      }
      else {
        this.width = maxTrailerWidth;
        this.height = 375;
      }

      try {
        this.changeDetectorRef.detectChanges();
      } catch (e) {
        // Guard against detectChanges failing when view is destroyed during resize
      }
    }
  }

  ngOnInit() {
    if (!this.trailer || !this.trailer.source) {
      return;
    }

    this.trailerSource = this.domSanitizer.bypassSecurityTrustResourceUrl("https://www.youtube.com/embed/" + this.trailer.source + "?rel=0&playsinline=1&fs=1");
  }

  ngAfterViewInit() {
    // Wait a bit for the container to be rendered
    this.ngZone.runOutsideAngular(() => {
      this.setupTimeout = setTimeout(() => {
        if (!this.destroyed) {
          this.ngZone.run(() => {
            this.updateTrailerSize();
          });
        }

        // Set up ResizeObserver for the scroll container if available
        const scrollContainer = this.getParentContainer("cm-container");
        if (scrollContainer && (window as any).ResizeObserver) {
          this.containerResizeObserver = new (window as any).ResizeObserver(() => {
            if (this.destroyed) { return; }
            // Debounce resize events
            if (this.resizeTimeout) {
              clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
              if (this.destroyed) { return; }
              this.ngZone.run(() => {
                this.updateTrailerSize();
              });
            }, 100);
          });
          this.containerResizeObserver.observe(scrollContainer);
        }
      }, 100);
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.setupTimeout) {
      clearTimeout(this.setupTimeout);
      this.setupTimeout = null;
    }
    if (this.containerResizeObserver) {
      this.containerResizeObserver.disconnect();
      this.containerResizeObserver = null;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event){
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updateTrailerSize();
    }, 250);
  }

}
